// B2-GATES (planned-outputs §3, batch review): a DE's `task_batch` approval — N tasks of a board
// it asks to move to one status — decided row by row: Keep (moves), Edit (title or date, then
// moves), Send back (stays, the note goes to the DE on the task) or Drop (to the board's drop
// status, or stays with the reason). One "Apply" decides the lot: all kept is a plain approve,
// anything else is "approve with edits" carrying each row's decision. Decided, it reads back
// what each row became.
import { Badge, Button, cn, InfoTip, Input } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightIcon, CheckIcon, PencilIcon, Trash2Icon, Undo2Icon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { ApprovalRow } from "../../api/types";
import { chatKeys } from "../chat/chat-queries";
import { problemText } from "../de/de-status";
import { formatDate } from "../format";
import { boardQuery } from "../work/work-queries";
import { agentKeys, invalidateDecided } from "./agent-queries";

type Decision = "keep" | "edit" | "send_back" | "drop";
type Item = {
  task_id: string;
  version: number;
  number: number;
  title: string;
  from_status: string;
  due_at: string | null;
  decision?: Decision;
  note?: string;
  edit?: { title?: string; due_at?: string | null };
};
type Batch = {
  board_id: string;
  to_status: string;
  drop_status: string | null;
  items: Item[];
};

const DECISIONS: { id: Decision; label: string; Icon: typeof CheckIcon }[] = [
  { id: "keep", label: "Keep", Icon: CheckIcon },
  { id: "edit", label: "Edit", Icon: PencilIcon },
  { id: "send_back", label: "Send back", Icon: Undo2Icon },
  { id: "drop", label: "Drop", Icon: Trash2Icon },
];
const TONE: Record<Decision, "success" | "info" | "warning" | "secondary"> = {
  keep: "success",
  edit: "info",
  send_back: "warning",
  drop: "secondary",
};

const dateOf = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : "");

export function TaskBatchReview({
  api,
  co,
  approval: a,
  canDecide,
}: {
  api: Api;
  co: string;
  approval: ApprovalRow;
  /** The server's `can_decide` (pending, not expired, the caller decides). */
  canDecide: boolean;
}) {
  const qc = useQueryClient();
  const batch = a.proposal.change as unknown as Batch;
  const decided = (a.approved_payload?.change ?? null) as unknown as Batch | null;
  const board = useQuery(boardQuery(api, co, batch.board_id));
  const statusName = (key: string | null) =>
    key === null ? "—" : (board.data?.status_set.find((s) => s.key === key)?.name ?? key);
  const [rows, setRows] = useState<Item[]>(() =>
    batch.items.map((i) => ({ ...i, decision: "keep" as Decision })),
  );
  // the decider decides; anyone else (the requester, an admin viewing) reads the rows
  const open = a.state === "pending" && canDecide;
  const set = (id: string, patch: Partial<Item>) =>
    setRows((list) => list.map((r) => (r.task_id === id ? { ...r, ...patch } : r)));
  const settled = (d: ApprovalRow) => {
    qc.setQueryData(agentKeys.approval(co, d.id), d);
    qc.setQueryData(chatKeys.approval(co, d.id), d);
  };
  const apply = useMutation({
    mutationFn: () => {
      const plain = rows.every((r) => r.decision === "keep");
      if (plain) return api.client.approvals.approve({ params: { co, id: a.id } });
      const items = rows.map(({ decision, note, edit, ...rest }) => ({
        ...rest,
        decision: decision ?? "keep",
        ...(note?.trim() && decision !== "keep" ? { note: note.trim() } : {}),
        ...(decision === "edit" && edit ? { edit } : {}),
      }));
      return api.client.approvals.edit({
        params: { co, id: a.id },
        body: {
          proposal: {
            title: a.proposal.title,
            ...(a.proposal.summary ? { summary: a.proposal.summary } : {}),
            change: { ...a.proposal.change, items },
          },
        },
      });
    },
    onSuccess: settled,
    onSettled: () => invalidateDecided(qc, co),
  });
  const counts = rows.reduce<Record<Decision, number>>(
    (m, r) => {
      m[r.decision ?? "keep"] += 1;
      return m;
    },
    { keep: 0, edit: 0, send_back: 0, drop: 0 },
  );
  const missingNote = rows.some((r) => r.decision === "send_back" && !r.note?.trim());
  const shown: Item[] = open
    ? rows
    : (decided?.items ??
      batch.items.map((i) => ({
        ...i,
        decision: (a.state === "rejected" ? "send_back" : "keep") as Decision,
      })));
  return (
    <div data-slot="task-batch" className="flex flex-col gap-3 text-sm">
      <p className="m-0 flex items-center gap-1.5 text-muted-foreground">
        <span>{shown.length} tasks</span>
        <ArrowRightIcon aria-hidden="true" className="size-3.5" />
        <span className="font-medium text-foreground">{statusName(batch.to_status)}</span>
        <InfoTip label="About batch review">
          Keep moves the task. Edit changes its title or date, then moves it. Send back leaves it
          where it is and gives the DE your note. Drop moves it to{" "}
          {batch.drop_status ? statusName(batch.drop_status) : "nowhere (it stays, noted)"}.
        </InfoTip>
      </p>
      <ul aria-label="Tasks in this review" className="m-0 flex list-none flex-col gap-1 p-0">
        {shown.map((r) => {
          const d = r.decision ?? "keep";
          return (
            <li
              key={r.task_id}
              data-task={r.task_id}
              data-decision={d}
              className="group/row flex flex-col gap-1.5 rounded-md border border-subtle px-3 py-2 hover:bg-muted/50"
            >
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                  #{r.number}
                </span>
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate",
                    d === "drop" && "text-muted-foreground line-through",
                  )}
                  title={r.title}
                >
                  {r.edit?.title ?? r.title}
                </span>
                <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                  {statusName(r.from_status)}
                  {r.due_at || r.edit?.due_at
                    ? ` · due ${formatDate(r.edit?.due_at ?? r.due_at ?? "")}`
                    : ""}
                </span>
                {open ? (
                  <span
                    role="radiogroup"
                    aria-label={`Decision for #${r.number}`}
                    className="flex shrink-0 items-center gap-0.5"
                  >
                    {DECISIONS.map(({ id, label, Icon }) => (
                      <Button
                        key={id}
                        role="radio"
                        aria-checked={d === id}
                        variant={d === id ? "secondary" : "ghost"}
                        size="icon-xs"
                        title={label}
                        aria-label={`${label} #${r.number}`}
                        onClick={() => set(r.task_id, { decision: id })}
                        className={cn(
                          d !== id &&
                            "opacity-40 group-hover/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
                        )}
                      >
                        <Icon aria-hidden="true" />
                      </Button>
                    ))}
                  </span>
                ) : (
                  <Badge variant={TONE[d]} data-decision={d}>
                    {DECISIONS.find((x) => x.id === d)?.label}
                  </Badge>
                )}
              </div>
              {open && d === "edit" ? (
                <div className="flex flex-wrap gap-2">
                  <Input
                    aria-label={`Title of #${r.number}`}
                    className="min-w-48 flex-1"
                    value={r.edit?.title ?? r.title}
                    onChange={(e) => set(r.task_id, { edit: { ...r.edit, title: e.target.value } })}
                  />
                  <Input
                    type="date"
                    aria-label={`Due date of #${r.number}`}
                    className="w-40"
                    value={dateOf(r.edit?.due_at !== undefined ? r.edit.due_at : r.due_at)}
                    onChange={(e) =>
                      set(r.task_id, {
                        edit: {
                          ...r.edit,
                          due_at: e.target.value ? `${e.target.value}T17:00:00.000Z` : null,
                        },
                      })
                    }
                  />
                </div>
              ) : null}
              {open && (d === "send_back" || d === "drop") ? (
                <Input
                  aria-label={`Note for #${r.number}`}
                  placeholder={
                    d === "send_back" ? "What to change (goes to the DE)" : "Why (optional)"
                  }
                  value={r.note ?? ""}
                  onChange={(e) => set(r.task_id, { note: e.target.value })}
                />
              ) : null}
              {!open && r.note ? (
                <p className="m-0 text-xs text-muted-foreground">Note: {r.note}</p>
              ) : null}
            </li>
          );
        })}
      </ul>
      {open ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground" data-slot="batch-counts">
            {DECISIONS.filter((x) => counts[x.id])
              .map((x) => `${counts[x.id]} ${x.label.toLowerCase()}`)
              .join(" · ")}
          </span>
          <Button
            className="ml-auto"
            disabled={apply.isPending || missingNote}
            title={missingNote ? "A row sent back needs a note" : undefined}
            onClick={() => apply.mutate()}
          >
            <CheckIcon aria-hidden="true" />
            {apply.isPending ? "Applying…" : "Apply review"}
          </Button>
        </div>
      ) : null}
      {apply.isError ? (
        <p role="alert" className="m-0 text-sm text-destructive">
          {problemText(apply.error, "The review could not be applied.")}
        </p>
      ) : null}
    </div>
  );
}
